import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { signOut } from "@store/auth";
import { MessageWizardResult, MessageWizardState } from "./messageWizard.types";
import {
  getQuestionnaire,
  rephrase,
  saveMessageWizardResults,
  sendFeedback,
} from "./messageWizard.thunks";

const initialState: MessageWizardState = {
  isLoading: {
    questionnaire: false,
    rephrase: false,
    savedResults: false,
    feedback: false,
  },
  isError: {
    questionnaire: false,
    rephrase: false,
    savedResults: false,
    feedback: false,
  },
  isCompleted: false,
  isTextCopied: false,
  isPositiveFeedback: null,
  questions: [],
  savedResults: [],
  rephraseResult: null,
};

const messageWizardSlice = createSlice({
  initialState,
  name: "messageWizard",
  reducers: {
    saveMessageWizardAnswer: (
      state,
      action: PayloadAction<{ answer: MessageWizardResult; isCompleted: boolean }>
    ) => {
      const { answer, isCompleted } = action.payload;
      const questionIdToOverride =
        state.questions.find((question) => question.wqId === answer.questionId)?.overrides || null;

      const savedResultIndex = state.savedResults.findIndex(
        (result) => result.questionId === answer.questionId
      );

      const savedResultToOverrideIndex = state.savedResults.findIndex(
        (result) => result.questionId === questionIdToOverride
      );

      state.isCompleted = isCompleted;

      if (savedResultToOverrideIndex > -1) {
        state.savedResults[savedResultToOverrideIndex] = {
          questionId: state.savedResults[savedResultToOverrideIndex].questionId,
          answerId: answer.answerId,
        };
      } else if (savedResultIndex > -1) {
        state.savedResults[savedResultIndex] = answer;
      } else {
        state.savedResults.push(answer);
      }
    },
    copyRephraseResult: (state) => {
      state.isTextCopied = true;
    },
    clearRephraseResult: (state) => {
      state.isTextCopied = false;
      state.isPositiveFeedback = null;
      state.rephraseResult = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getQuestionnaire.pending, (state) => {
      state.isLoading.questionnaire = true;
      state.isError.questionnaire = false;
    });
    builder.addCase(getQuestionnaire.fulfilled, (state, action) => {
      const { questions, savedResults } = action.payload;
      state.isLoading.questionnaire = false;
      state.questions = questions;
      state.savedResults = savedResults.questionnaireResults || [];
      state.isCompleted = savedResults.questionnaireComplete;
    });
    builder.addCase(getQuestionnaire.rejected, (state, action) => {
      state.isLoading.questionnaire = false;
      state.isError.questionnaire = !action.meta.aborted;
    });
    builder.addCase(saveMessageWizardResults.pending, (state) => {
      state.isLoading.savedResults = true;
      state.isError.savedResults = false;
    });
    builder.addCase(saveMessageWizardResults.fulfilled, (state) => {
      state.isLoading.savedResults = false;
      state.isCompleted = false;
      state.isTextCopied = false;
      state.isPositiveFeedback = null;
      state.savedResults = [];
      state.rephraseResult = null;
    });
    builder.addCase(saveMessageWizardResults.rejected, (state, action) => {
      state.isLoading.savedResults = false;
      state.isError.savedResults = !action.meta.aborted;
    });
    builder.addCase(rephrase.pending, (state) => {
      state.isLoading.rephrase = true;
      state.isError.rephrase = false;
    });
    builder.addCase(rephrase.fulfilled, (state, action) => {
      state.isLoading.rephrase = false;
      state.rephraseResult = action.payload;
    });
    builder.addCase(rephrase.rejected, (state, action) => {
      state.isLoading.rephrase = false;
      state.isError.rephrase = !action.meta.aborted;
    });
    builder.addCase(sendFeedback.pending, (state, action) => {
      state.isLoading.feedback = true;
      state.isError.feedback = false;
      state.isPositiveFeedback = action.meta.arg.isPositive;
    });
    builder.addCase(sendFeedback.fulfilled, (state) => {
      state.isLoading.feedback = false;
    });
    builder.addCase(sendFeedback.rejected, (state, action) => {
      state.isLoading.feedback = false;
      state.isError.feedback = !action.meta.aborted;
    });
    builder.addCase(signOut.pending, (state) => {
      state.isCompleted = false;
      state.isTextCopied = false;
      state.isPositiveFeedback = null;
      state.questions = [];
      state.savedResults = [];
      state.rephraseResult = null;
    });
  },
});

export const { saveMessageWizardAnswer, copyRephraseResult, clearRephraseResult } =
  messageWizardSlice.actions;

export const messageWizardReducer = messageWizardSlice.reducer;
